import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Link,
  IconButton,
  InputAdornment,
  FormHelperText,
  Alert,
  Tooltip,
  Stack,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import "./../../theme.scss";
import "../../assets/styles/common-styles.scss";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import { usePasscode } from "../../hooks/usePasscode";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { resetPasswordSchema } from "../../config/schemas/forgotPasscodeSchema";
import { z } from "zod";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useNavigate } from "react-router-dom";
import { HelperTextComponent } from "../common/HelperTextComponent";
import { useAppDispatch } from "../../store";
import { showLoader, hideLoader } from "../../store/slices/loaderSlice";
import Loader from "../common/Loader/Loader";
import PasswordRequirement from "../common/PasswordRequirement";

const ResetPassword: React.FunctionComponent<any> = (props) => {
  const { setIsEmailPresent } = props;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { showSuccess, handleError } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const countdownTime = 20 * 60 * 1000; // 20 minutes in milliseconds
  const [remainingTime, setRemainingTime] = useState(countdownTime);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const email = localStorage?.getItem("passcodeEmail");

  const calculateRemainingTime = () => {
    const storedStartTime = localStorage.getItem("passcodeStartTime");
    const startTime = storedStartTime ? parseInt(storedStartTime) : Date.now();
    localStorage.setItem("passcodeStartTime", startTime.toString());
    const elapsedTime = Date.now() - startTime;
    return countdownTime - elapsedTime;
  };

  const {
    resetPassword,
    resetPasswordData,
    resetPasswordIsError,
    resetPasswordError,
    resetPasswordSuccess,
    getPasscode,
    passcodeData,
    passcodeIsError,
    passcodeError,
    passcodeIsSuccess,
    passcodeIsLoading,
  } = usePasscode();
  type FormValues = z.infer<typeof resetPasswordSchema>;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
  } = useForm<FormValues>({
    defaultValues: {},
    resolver: zodResolver(resetPasswordSchema),
  });
  const onInvalid = (errors: any) => console.debug(errors);

  const submit = (data: any) => {
    resetPassword({
      data: {
        email,
        new_password: data.new_password,
        passcode: data?.passcode,
      },
    });
  };

  const handleBack = () => {
    localStorage.removeItem("passcodeEmail");
    localStorage.removeItem("passcodeStartTime");
    setIsEmailPresent(false);
  };

  const getNewPasscode = () => {
    getPasscode({ email });
  };

  const startTimer = () => {
    const remaining = calculateRemainingTime();
    setRemainingTime(remaining > 0 ? remaining : 0);
    timerRef.current = setInterval(() => {
      const remaining = calculateRemainingTime();
      if (remaining <= 0) {
        if (timerRef.current !== null) {
          clearInterval(timerRef.current);
        }
        setRemainingTime(0);
      } else {
        setRemainingTime(remaining);
      }
    }, 1000);
  };

  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const formatTime = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  };

  useEffect(() => {
    if (resetPasswordSuccess) {
      const successMsg = resetPasswordData;
      showSuccess(successMsg?.details, "reset-password-success");
      localStorage.removeItem("passcodeEmail");
      localStorage.removeItem("passcodeStartTime");
      setIsEmailPresent(false);
      reset();
      navigate("/login");
    }
  }, [resetPasswordSuccess]);

  useEffect(() => {
    if (resetPasswordIsError) {
      const error: any = resetPasswordError;
      handleError(error);
    }
  }, [resetPasswordIsError]);

  useEffect(() => {
    if (passcodeIsSuccess) {
      const successMsg = passcodeData;
      showSuccess(successMsg?.details, "get-passcode-success");
      localStorage.setItem("passcodeStartTime", Date.now().toString());
      setRemainingTime(countdownTime);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      startTimer();
    }
  }, [passcodeIsSuccess]);

  useEffect(() => {
    if (passcodeIsError) {
      const error: any = passcodeError;
      handleError(error);
    }
  }, [passcodeIsError]);

  useEffect(() => {
    if (passcodeIsLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
  }, [passcodeIsLoading]);

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <Loader />
        <Typography gutterBottom>
          Passcode sent successfully to your email:
        </Typography>
        <form onSubmit={handleSubmit(submit, onInvalid)}>
          <Typography paragraph gutterBottom>
            <Tooltip
              placement="top"
              arrow
              title={`${email}`.length > 60 ? `${email}` : null}
            >
              <strong className="textTruncate dInlineBlock">
                {email ?? ""}
              </strong>
            </Tooltip>
          </Typography>
          <Alert severity="warning">
            <strong>
              Don&apos;t leave or refresh the page until passcode is entered.
            </strong>
          </Alert>
          <Controller
            control={control}
            {...register("passcode")}
            render={({ field: { ref, ...newField } }) => (
              <TextField
                {...newField}
                margin="dense"
                fullWidth
                {...register("passcode")}
                id="passcode"
                label="Passcode*"
                variant="standard"
                inputProps={{
                  maxLength: 8,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  form: { autocomplete: "off" },
                }}
                error={!!errors.passcode}
                onKeyDown={(e: any) => {
                  const charCode = e.which ? e.which : e.keyCode;
                  if (
                    (!e.ctrlKey &&
                      !e.metaKey &&
                      charCode > 40 &&
                      (charCode < 48 || charCode > 57)) ||
                    ["!", "@", "#", "$", "%", "^", "&", "*", "(", ")"].includes(
                      e.key
                    )
                  ) {
                    e.preventDefault();
                  }
                }}
                helperText={
                  errors?.passcode?.message ||
                  (newField?.value?.length === 8 && (
                    <HelperTextComponent mlength={8} />
                  ))
                }
              />
            )}
          />
          <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            alignItems="center"
          >
            <FormHelperText>
              Time remaining to enter passcode {formatTime(remainingTime)}
            </FormHelperText>
            <Link href="#" variant="body1" mt={1} onClick={getNewPasscode}>
              <strong>Request new passcode</strong>
            </Link>
          </Stack>
          <TextField
            margin="normal"
            fullWidth
            label="New Password*"
            type={showPassword ? "text" : "password"}
            id="new_password"
            variant="standard"
            {...register("new_password")}
            error={!!errors.new_password}
            helperText={errors.new_password?.message}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((show) => !show)}
                    onMouseDown={(event) => event.preventDefault()}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <PasswordRequirement />
          <TextField
            fullWidth
            label="Confirm Password*"
            type={showConfirmPassword ? "text" : "password"}
            id="confirmpassword"
            variant="standard"
            {...register("confirm_password")}
            error={!!errors.confirm_password}
            helperText={errors.confirm_password?.message}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowConfirmPassword((show) => !show)}
                    onMouseDown={(event) => event.preventDefault()}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Grid container alignItems="center" className="mt4">
            <Grid item xs>
              <Button variant="outlined" id="backReset" onClick={handleBack}>
                Back
              </Button>
            </Grid>
            <Grid item>
              <Button type="submit" variant="contained" id="savePWD">
                Save
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </>
  );
};

export default ResetPassword;
